import type { FC } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Typography,
  Box,
  Stack,
  IconButton,
  Divider,
  Autocomplete,
  InputAdornment,
  ButtonBase,
  type TextFieldProps,
  TextField,
  useTheme,
} from "@mui/material";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import { useForm, useFieldArray, Controller, useController, useFormContext } from "react-hook-form";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useTranslation } from "@privacy/translations";
import { InfoIcon, XIcon } from "../../shared/icons";
import { HoverPopover, useHoverPopover } from "../../shared/HoverPopover";
import { Flag } from "../../shared/Flag";
import { AutocompleteOption } from "../../shared/Autocomplete";
import { useDynamicFormState } from "../DynamicFormProvider";
import type { InputProps } from "../DynamicInput";
import { MultipleSubSupplierValueSchema, type MultipleSubSupplierInput, type MultipleSubSupplierValue } from "../types";
import { AddDialogTrigger } from "./AddDialogTrigger";
import { DynamicShared } from "../../dynamicShared";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";
import { useMutation } from "@tanstack/react-query";
import * as FormDialog from "../../shared/FormDialog";

type MultipleSubSupplierInputBaseProps = InputProps<MultipleSubSupplierValue> & {
  countryOptions: MultipleSubSupplierInput["countryOptions"];
};

const NUMBER_OF_VISIBLE_ITEMS = 7;

export const MultipleSubSupplierInputBase: FC<MultipleSubSupplierInputBaseProps> = ({
  questionCard,
  onSave,
  readonly,
  countryOptions,
}) => {
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();
  const { control } = useFormContext();
  const { field } = useController({ control, name: questionCard.id });

  const fieldValue = MultipleSubSupplierValueSchema.parse(field.value ?? []);

  const visibleSubsuppliers = fieldValue.slice(0, NUMBER_OF_VISIBLE_ITEMS);
  const hiddenSubsuppliers = fieldValue.slice(NUMBER_OF_VISIBLE_ITEMS);

  return (
    <Stack sx={{ mb: 0.5 }}>
      <AddDialogTrigger
        isEmpty={fieldValue.length === 0}
        renderDialog={props => (
          <MultipleSubSupplierInputDialog
            {...props}
            initialSubsuppliers={fieldValue}
            onSave={onSave}
            countryOptions={countryOptions}
            readonly={readonly}
          />
        )}
        renderTrigger={({ onClick }) => (
          <ButtonBase
            onClick={onClick}
            disabled={readonly && fieldValue.length === 0}
            sx={{
              "width": "100%",
              "backgroundColor": "transparent",
              "minHeight": "36px",
              "py": 0.5,
              "px": 2,
              "display": "flex",
              "alignItems": "center",
              "gap": 0.5,
              "flexWrap": "wrap",
              "justifyContent": "flex-start",
              "&:focus-visible": { outline: "2px solid", outlineColor: theme => theme.palette.primary["500"] },
              "&:hover": { backgroundColor: theme => theme.palette.action.hover },
              "borderRadius": 1,
            }}
          >
            {visibleSubsuppliers.map((subsupplier, index) => (
              <DynamicShared.Chip
                variant="choice"
                key={`${subsupplier.name}-${subsupplier.organisationNumber}-${index}`}
                label={subsupplier.name}
              />
            ))}
            {hiddenSubsuppliers.length > 0 && (
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  border: "1px solid",
                  px: 1,
                  py: "2px",
                  backgroundColor: theme => theme.palette.background.paper,
                  borderColor: theme => theme.palette.text.tertiary,
                  fontSize: theme => theme.typography.pxToRem(12),
                  fontWeight: "700",
                }}
              >
                +{hiddenSubsuppliers.length}
              </Stack>
            )}
          </ButtonBase>
        )}
      />
      {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(fieldValue) && (
        <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
      )}
    </Stack>
  );
};

type MultipleSubSupplierInputDialogProps = {
  onClose: () => void;
  initialSubsuppliers: MultipleSubSupplierValue;
  onSave: (subsuppliers: MultipleSubSupplierValue) => Promise<unknown>;
  countryOptions: MultipleSubSupplierInput["countryOptions"];
  readonly: boolean;
};

const HeaderColumnWithInfo: FC<{ label: string; infoBody: string }> = ({ label, infoBody }) => {
  const { anchorProps, popoverProps } = useHoverPopover();
  return (
    <Box>
      <Typography sx={{ lineHeight: 1, fontSize: "14px", fontWeight: "600", display: "inline-flex", gap: 0.5 }}>
        {label}
        <Box
          component="span"
          {...anchorProps}
          sx={{
            "position": "relative",
            "&:after": { content: '""', position: "absolute", inset: 0, transform: "scaleY(3) scaleX(2)" },
          }}
        >
          <InfoIcon strokeWidth={2.5} size={10} color={theme => theme.palette.primary.main} />
        </Box>
      </Typography>
      <HoverPopover {...popoverProps}>
        <Stack spacing={1} sx={{ px: 3, py: 2, maxWidth: "26rem" }}>
          <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {infoBody}
          </Typography>
        </Stack>
      </HoverPopover>
    </Box>
  );
};

const REMOVE_BUTTON_CLASS = "remove-button";

const MultipleSubSupplierInputDialog: FC<MultipleSubSupplierInputDialogProps> = ({
  onClose,
  initialSubsuppliers,
  onSave,
  countryOptions,
  readonly,
}) => {
  const { t } = useTranslation();
  const saveMutation = useMutation({
    mutationFn: (value: MultipleSubSupplierValue) => onSave(value),
  });

  const {
    control,
    handleSubmit,
    setFocus,
    getValues,
    formState: { isDirty },
  } = useForm<{
    subsuppliers: MultipleSubSupplierValue;
  }>({
    defaultValues: { subsuppliers: initialSubsuppliers },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "subsuppliers" });
  const [animationRef] = useAutoAnimate();

  const hasSubsupplierAnyValue = (subsupplier: MultipleSubSupplierValue[number]) => {
    return subsupplier.name || subsupplier.organisationNumber || subsupplier.country;
  };

  const onSubmit = ({ subsuppliers }: { subsuppliers: MultipleSubSupplierValue }) => {
    const nonEmptySubsuppliers = subsuppliers
      .map(subsupplier => ({
        name: subsupplier.name,
        organisationNumber: subsupplier.organisationNumber || undefined,
        country: subsupplier.country || undefined,
      }))
      .filter(hasSubsupplierAnyValue);
    saveMutation.mutateAsync(nonEmptySubsuppliers, { onSuccess: () => onClose() });
  };

  const addNewRow = () => {
    const newRow = { name: "", organisationNumber: "", country: "" };
    append(newRow);
    setFocus(`subsuppliers.${fields.length}.name`);
  };

  return (
    <Dialog
      open
      maxWidth="lg"
      slotProps={{ paper: { sx: { px: 0, py: 0, width: "1000px", height: "min(95vh, 900px)", overflowX: "hidden" } } }}
    >
      <DialogTitle
        sx={{
          pt: 6,
          px: 6,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: theme => theme.palette.background.paper,
        }}
      >
        {t("DynamicForm.MultipleSubSupplierInput.DialogTitle")}
      </DialogTitle>
      <Box sx={{ px: 6 }}>
        <FormDialog.FormError isError={saveMutation.isError} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "1fr 1fr 1fr 50px",
          px: 6,
          backgroundColor: theme => theme.palette.background.paper,
          borderBottom: "1px solid",
          borderColor: "divider",
          zIndex: 1,
          py: 1,
        }}
      >
        <HeaderColumnWithInfo
          label={t("DynamicForm.MultipleSubSupplierInput.Name")}
          infoBody={t("DynamicForm.MultipleSubSupplierInput.Name.InfoPopover")}
        />
        <HeaderColumnWithInfo
          label={t("DynamicForm.MultipleSubSupplierInput.OrganisationNumber")}
          infoBody={t("DynamicForm.MultipleSubSupplierInput.OrganisationNumber.InfoPopover")}
        />
        <HeaderColumnWithInfo
          label={t("DynamicForm.MultipleSubSupplierInput.Country")}
          infoBody={t("DynamicForm.MultipleSubSupplierInput.Country.InfoPopover")}
        />
      </Box>
      <Stack component="form" sx={{ flex: 1 }}>
        <Stack
          sx={{ position: "relative", backgroundColor: theme => theme.palette.background.paper, flex: 1, minHeight: 0 }}
        >
          {fields.length === 0 ? (
            <Stack sx={{ flex: 1, alignItems: "center", gap: 4, justifyContent: "center" }}>
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: theme => theme.typography.pxToRem(16),
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {t("DynamicForm.MultipleSubSupplierInput.EmptyState")}
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={addNewRow}
                sx={{ fontSize: "12px", fontWeight: 600 }}
              >
                {t("DynamicForm.MultipleSubSupplierInput.AddFirst")}
              </Button>
            </Stack>
          ) : (
            <>
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  backgroundColor: theme => theme.palette.background.paper,
                  padding: 0,
                  margin: 0,
                }}
                ref={animationRef}
              >
                {fields.map((fieldItem, index) => (
                  <li key={fieldItem.id}>
                    <Box
                      sx={{
                        "alignContent": "center",
                        "borderRadius": 0.5,
                        "borderColor": "divider",
                        "py": 0.5,
                        "& .remove-button": { opacity: 0, transition: "opacity 0.1s" },
                        [`&:is(:hover, :focus-within)`]: { backgroundColor: theme => theme.palette.action.hover },
                        [`&:is(:hover, :focus-within) .${REMOVE_BUTTON_CLASS}, & .${REMOVE_BUTTON_CLASS}:focus-visible`]:
                          { opacity: 1 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gap: 2,
                          pl: 6,
                          pr: 6,
                          gridTemplateColumns: "1fr 1fr 1fr 50px",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Controller
                            control={control}
                            name={`subsuppliers.${index}.name`}
                            rules={{
                              validate: value => {
                                const row = getValues(`subsuppliers.${index}`);
                                if (!hasSubsupplierAnyValue(row)) return true;
                                return Boolean(value);
                              },
                            }}
                            render={({ field: { ref, ...field }, fieldState }) => (
                              <SubSupplierTextField
                                {...field}
                                inputRef={ref}
                                error={fieldState.invalid}
                                placeholder={t("DynamicForm.MultipleSubSupplierInput.Dialog.FieldPlaceholder")}
                                disabled={readonly}
                              />
                            )}
                          />
                        </Box>
                        <Box>
                          <Controller
                            control={control}
                            name={`subsuppliers.${index}.organisationNumber`}
                            render={({ field: { ref, ...field } }) => (
                              <SubSupplierTextField
                                {...field}
                                inputRef={ref}
                                placeholder={t("DynamicForm.MultipleSubSupplierInput.Dialog.FieldPlaceholder")}
                                disabled={readonly}
                              />
                            )}
                          />
                        </Box>
                        <Box>
                          <Controller
                            control={control}
                            name={`subsuppliers.${index}.country`}
                            render={({ field: { ref, ...field } }) => {
                              const selectedCountry = countryOptions.find(c => c.value === field.value);
                              return (
                                <Autocomplete
                                  {...field}
                                  size="small"
                                  fullWidth
                                  disabled={readonly}
                                  options={countryOptions}
                                  getOptionLabel={option => option.label}
                                  value={selectedCountry ?? null}
                                  onChange={(_, newValue) => field.onChange(newValue?.value ?? "")}
                                  renderInput={params => (
                                    <SubSupplierTextField
                                      {...params}
                                      inputRef={ref}
                                      placeholder={t("DynamicForm.MultipleSubSupplierInput.Dialog.FieldPlaceholder")}
                                      slotProps={{
                                        input: {
                                          ...params.slotProps.input,
                                          startAdornment: selectedCountry ? (
                                            <InputAdornment position="start">
                                              <Flag countryCode={selectedCountry.value} />
                                            </InputAdornment>
                                          ) : (
                                            params.slotProps.input.startAdornment
                                          ),
                                        },
                                      }}
                                      sx={{
                                        [`& .${autocompleteClasses.endAdornment}`]: { opacity: 0 },
                                        [`&:hover: *:not(.Mui-disabled) .${autocompleteClasses.endAdornment}, &:focus-within .${autocompleteClasses.endAdornment}`]:
                                          { opacity: 1 },
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <AutocompleteOption
                                      {...props}
                                      option={option}
                                      icon={<Flag countryCode={option.value} />}
                                    />
                                  )}
                                />
                              );
                            }}
                          />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {!readonly && (
                            <IconButton
                              aria-label={t("DynamicForm.MultipleSubSupplierInput.Remove")}
                              className={REMOVE_BUTTON_CLASS}
                              size="small"
                              sx={{ position: "sticky", bottom: 200, marginLeft: "auto" }}
                              onClick={() => remove(index)}
                            >
                              <XIcon size={16} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Divider />
                  </li>
                ))}
              </Box>
              <Box
                sx={{
                  mt: 1.5,
                  mx: 3,
                  pb: 6,
                  display: "flex",
                  alignSelf: "start",
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: theme => theme.palette.background.paper,
                  zIndex: 1000000,
                }}
              >
                {!readonly && (
                  <Button type="button" variant="text" sx={{ fontSize: "12px", fontWeight: 600 }} onClick={addNewRow}>
                    {t("DynamicForm.MultipleSubSupplierInput.AddNew")}
                  </Button>
                )}
              </Box>
            </>
          )}
        </Stack>
      </Stack>
      <DialogActions
        sx={{
          px: 6,
          pb: 6,
          pt: 3,
          m: 0,
          position: "sticky",
          bottom: 0,
          backgroundColor: theme => theme.palette.background.paper,
        }}
      >
        <Button type="button" variant="outlined" onClick={onClose}>
          {t("DynamicForm.MultipleSubSupplierInput.CancelButton")}
        </Button>
        {!readonly && (
          <Button
            disabled={!isDirty}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            loading={saveMutation.isPending}
          >
            {t("DynamicForm.MultipleSubSupplierInput.SaveButton")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const SubSupplierTextField: FC<TextFieldProps> = props => {
  const theme = useTheme();

  return (
    <TextField
      size="small"
      placeholder="-"
      fullWidth
      {...props}
      slotProps={{
        input: {
          ...props.slotProps?.input,
          sx: {
            "ml": -1.75,
            "& input:focus::-webkit-input-placeholder": { opacity: 0 },

            // Background: transparent by default, white on hover/focus
            "backgroundColor": "transparent !important",
            "&:hover:not(.Mui-disabled), &.Mui-focused": {
              backgroundColor: `${theme.palette.background.paper} !important`,
            },
            // Border: hidden by default, primary color on hover/focus, error color when error
            "& fieldset": { borderColor: "transparent !important" },
            "&:hover:not(.Mui-disabled) fieldset, &.Mui-focused fieldset": {
              borderColor: `${theme.palette.primary["500"]} !important`,
            },
            "&.Mui-error fieldset": {
              borderColor: `${theme.palette.error.main} !important`,
            },
          },
        },
      }}
    />
  );
};
